/**
 * @module utils/getTotalAnalysesRemainingParts
 * @description
 *   Total runs left + Option B analyses meter (primaryTier + paid high-water).
 *
 * @invariants
 *   - Consumption order matches server billing (subscription → paid → free → daily)
 *   - Meter fill uses primaryTier only; badge number may still sum all pools
 *   - Daily grants are disabled product-wide; still summed if present for backward compat
 *
 * @see docs/ANALYSES_METER_IMPLEMENTATION_PLAN.md
 * @see docs/BILLING_AND_STRIPE.md
 */

const FREE_ALLOTMENT = 3;

/**
 * @param {Object} parts
 * @param {boolean} parts.hasActiveSubscription
 * @param {number} parts.subscriptionAnalysesLimit
 * @param {number} parts.subscriptionAnalysesUsed
 * @param {number} parts.paidAnalyses
 * @param {number} parts.submissionCount
 * @param {number} [parts.dailyAnalysesAvailable]
 * @returns {{ subRem: number, paidRem: number, freeRem: number, dailyRem: number }}
 */
function getAnalysesRemainderBuckets({
  hasActiveSubscription,
  subscriptionAnalysesLimit,
  subscriptionAnalysesUsed,
  paidAnalyses,
  submissionCount,
  dailyAnalysesAvailable,
}) {
  const subRem =
    hasActiveSubscription && subscriptionAnalysesLimit > 0
      ? Math.max(0, subscriptionAnalysesLimit - subscriptionAnalysesUsed)
      : 0;
  const paidRem = Math.max(0, paidAnalyses || 0);
  const freeRem = submissionCount < FREE_ALLOTMENT ? FREE_ALLOTMENT - submissionCount : 0;
  const dailyRem = Math.max(0, dailyAnalysesAvailable || 0);
  return { subRem, paidRem, freeRem, dailyRem };
}

/** Total runs left: subscription pool + pay-per-use credits + free tier + daily grant. */
function getTotalAnalysesRemainingParts(parts) {
  const { subRem, paidRem, freeRem, dailyRem } = getAnalysesRemainderBuckets(parts);
  return subRem + paidRem + freeRem + dailyRem;
}

/**
 * Resolve paid high-water for metering. Missing/invalid → treat as current paid balance.
 * @param {number} paidAnalyses
 * @param {number} [paidAnalysesHighWater]
 * @returns {number}
 */
function resolvePaidHighWater(paidAnalyses, paidAnalysesHighWater) {
  const paidRem = Math.max(0, paidAnalyses || 0);
  if (typeof paidAnalysesHighWater === 'number' && Number.isFinite(paidAnalysesHighWater)) {
    return Math.max(0, paidAnalysesHighWater, paidRem);
  }
  return paidRem;
}

/**
 * Display tier for the header meter (Option B). Not an authorization role.
 * @returns {'subscriber'|'credits'|'free'|'exhausted'}
 */
function getPrimaryTier({
  hasActiveSubscription,
  paidAnalyses,
  submissionCount,
}) {
  if (hasActiveSubscription) return 'subscriber';
  if (Math.max(0, paidAnalyses || 0) > 0) return 'credits';
  if (submissionCount < FREE_ALLOTMENT) return 'free';
  return 'exhausted';
}

/**
 * @param {Object} parts - Same shape as getTotalAnalysesRemainingParts, plus optional paidAnalysesHighWater
 * @returns {{
 *   remaining: number,
 *   primaryTier: 'subscriber'|'credits'|'free'|'exhausted',
 *   isSubscriber: boolean,
 *   meter: { value: number, max: number, percent: number }
 * }}
 */
function getAnalysesMeterParts(parts) {
  const {
    hasActiveSubscription,
    subscriptionAnalysesLimit,
    subscriptionAnalysesUsed,
    paidAnalyses,
    submissionCount,
    paidAnalysesHighWater,
  } = parts;

  const remaining = getTotalAnalysesRemainingParts(parts);
  const isSubscriber = !!hasActiveSubscription;
  const primaryTier = getPrimaryTier({
    hasActiveSubscription,
    paidAnalyses,
    submissionCount,
  });

  let value = 0;
  let max = 0;

  if (primaryTier === 'subscriber') {
    max = Math.max(0, subscriptionAnalysesLimit || 0);
    value =
      max > 0
        ? Math.max(0, max - Math.max(0, subscriptionAnalysesUsed || 0))
        : 0;
  } else if (primaryTier === 'credits') {
    value = Math.max(0, paidAnalyses || 0);
    max = resolvePaidHighWater(paidAnalyses, paidAnalysesHighWater);
  } else if (primaryTier === 'free') {
    max = FREE_ALLOTMENT;
    value = Math.max(0, FREE_ALLOTMENT - Math.max(0, submissionCount || 0));
  }

  const percent = max > 0 ? Math.min(100, (value / max) * 100) : 0;

  return {
    remaining,
    primaryTier,
    isSubscriber,
    meter: { value, max, percent },
  };
}

export default getTotalAnalysesRemainingParts;
export {
  FREE_ALLOTMENT,
  getPrimaryTier,
  getAnalysesMeterParts,
  resolvePaidHighWater,
  getAnalysesRemainderBuckets,
};
